import {useCallback, useContext, useMemo, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {useStore} from '@tanstack/react-form'

import Context from '@s-ui/react-context'
import FormCaptcha from '@s-ui/react-form-captcha'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useCaptcha} from '@adv-mt/mt-hook-use-captcha'
import BlockedUserToast from '@adv-mt/mt-notifications-blocked-user-toast'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Body1} from '@adv-mt/ui/typography'

import {useContactCardInput, useContactCardState, useContactSellerV2Config} from '../context/ContactSellerV2Context.js'
import {FORM_PRESENTATIONS} from '../contracts.js'
import {useAppForm} from '../form/formHook.js'
import {removeBlockedUser} from '../helpers/blockedUsers.js'
import useAlertAfterLead from '../hooks/useAlertAfterLead.js'
import useBuyerIntentionsRestore from '../hooks/useBuyerIntentionsRestore.js'
import useBuyerIntentionsStorage from '../hooks/useBuyerIntentionsStorage.js'
import {useContactSubmit} from '../hooks/useContactSubmit.js'
import useDrawerPlacement from '../hooks/useDrawerPlacement.js'
import useLastContactPrefill from '../hooks/useLastContactPrefill.js'
import usePresetMessageSync from '../hooks/usePresetMessageSync.js'
import useSubmitErrorNotification from '../hooks/useSubmitErrorNotification.js'
import useTargetBuyerSync from '../hooks/useTargetBuyerSync.js'
import useUserDataPrefill from '../hooks/useUserDataPrefill.js'
import {
  createContactSchema,
  DEFAULT_TARGET_BUYER,
  hasMissingRequiredFields,
  V2_FIELDS,
  validateField
} from '../schema/contactSchema.js'
import ContactBody, {getMessageTitleKey} from './ContactBody.js'
import {ContactFormContextProvider} from './ContactFormContext.js'
import FormDrawer from './FormDrawer.js'
import {BASE_CLASS} from './FormFields.js'
import SuccessState, {resolveSuccessLiteral} from './SuccessState.js'

export const FORM_STATES = {
  IDLE: 'IDLE',
  PREFILLED: 'PREFILLED',
  DIRTY: 'DIRTY',
  VALIDATING: 'VALIDATING',
  ERROR_FIELDS: 'ERROR_FIELDS',
  SUBMITTING: 'SUBMITTING',
  CONTACTED: 'CONTACTED',
  ERROR_GENERIC: 'ERROR_GENERIC',
  BLOCKED: 'BLOCKED'
}

export const resolveFormState = ({
  isBlocked = false,
  hasContacted = false,
  hasGenericError = false,
  isSubmitting = false,
  isValidating = false,
  hasFieldErrors = false,
  isDirty = false,
  isPrefilled = false
}) => {
  if (isBlocked) {
    return FORM_STATES.BLOCKED
  }
  if (hasContacted) {
    return FORM_STATES.CONTACTED
  }
  if (hasGenericError) {
    return FORM_STATES.ERROR_GENERIC
  }
  if (isSubmitting) {
    return FORM_STATES.SUBMITTING
  }
  if (isValidating) {
    return FORM_STATES.VALIDATING
  }
  if (hasFieldErrors) {
    return FORM_STATES.ERROR_FIELDS
  }
  if (isDirty) {
    return FORM_STATES.DIRTY
  }

  return isPrefilled ? FORM_STATES.PREFILLED : FORM_STATES.IDLE
}

const EMPTY = ''

const submitBehindLoginGate = async ({
  value,
  captchaTokenRef,
  submitContact,
  isPrivateSeller,
  isLogged,
  isLoginModalOpen,
  config,
  triggerLoginModal,
  authenticationHitInformation2
}) => {
  const proceedWithLead = () => submitContact(value, {captchaToken: captchaTokenRef.current})

  if (isPrivateSeller && !isLogged && !isLoginModalOpen) {
    const {LOGIN_SOURCE_ID_LEAD} = config.get('login')
    const {HIT_ORIGINS} = config.get('tracking')

    triggerLoginModal({
      modalProps: {
        source: LOGIN_SOURCE_ID_LEAD,
        trackingOrigin: HIT_ORIGINS.LEAD_EMAIL,
        hitInformation2: authenticationHitInformation2,
        onLogin: proceedWithLead,
        onClose: () => {}
      }
    })

    return
  }

  await proceedWithLead()
}

const unblockSellerAndResubmit = async ({
  domain,
  config,
  logger,
  adId,
  pageName,
  sellerId,
  setHasUnblockError,
  setIsBlocked,
  resetResult,
  submitForm
}) => {
  setHasUnblockError(false)

  const [error] = await domain.get('unblock_user_use_case').execute({userToUnblockId: sellerId})

  if (error) {
    logger?.error({
      name: `[ContactSeller]: "adId: ${adId}" unblock failed in page: ${pageName}`,
      message: `[ContactSeller]: ${error?._message}, status: ${error?._status}`
    })
    setHasUnblockError(true)

    return
  }

  setIsBlocked(false)
  removeBlockedUser({cookieKey: config.get('COMMON').BLOCKED_USERS_KEY, userId: sellerId})

  resetResult()
  submitForm()
}

const BLOCKED_TOAST_STATUS = {BLOCKED: 'system', UNBLOCKED: 'success'}

const ContactForm = ({buttonGroup}) => {
  const {
    alertSearch,
    formPresentation = FORM_PRESENTATIONS.INLINE,
    isZipCodeEnabled = false,
    showTargetBuyerOptions = false,
    targetBuyer,
    subscriptionCommitment,
    enableAlertCreation = false,
    isSellerBlocked = false,
    sellerId,
    userData,
    isLeadEnrichmentEnabled = false,
    isVOorVNorKM0 = false,
    isRenting = false,
    isDrawerOpen,
    onDrawerOpenChange,
    presetBuyerIntentions,
    presetMessage
  } = useContactCardInput()
  const {
    adId,
    authenticationHitInformation2,
    contractId,
    pageName,
    enableAuthBeforeLead = false,
    isPrivateSeller = false,
    hidePhone = false,
    onAlertCreated,
    onContact,
    onLeadError,
    onSendMessageClick,
    onSubmitError
  } = useContactSellerV2Config()
  const {setBuyerIntentionsSelection: onBuyerIntentionsChange} = useContactCardState()
  const {i18n, domain, logger} = useContext(Context)
  const {isLogged} = useContext(AuthenticationContext)
  const {triggerLoginModal, isLoginModalOpen} = useContext(LoginModalContext)
  const config = domain.get('config')
  const {isBottomSheet} = useDrawerPlacement()
  const isBottomDrawer = formPresentation === FORM_PRESENTATIONS.DRAWER && isBottomSheet

  const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(false)
  const isDrawerControlled = isDrawerOpen !== undefined
  const isOpen = isDrawerControlled ? isDrawerOpen : isOpenUncontrolled
  const openDrawerTriggerRef = useRef(null)
  const {isActive: isCaptchaActive, siteKey: captchaSiteKey} = useCaptcha()
  const captchaTokenRef = useRef(EMPTY)
  const [isPrefilled, setIsPrefilled] = useState(Boolean(userData))
  const [isBlocked, setIsBlocked] = useState(isSellerBlocked)
  const [isBlockedToastVisible, setIsBlockedToastVisible] = useState(isSellerBlocked)
  const [hasUnblockError, setHasUnblockError] = useState(false)
  const [blockedSubmitAttempts, setBlockedSubmitAttempts] = useState(0)

  const {submitContact, resetResult, clearFieldError, hasContacted, hasGenericError, hasCaptchaError, fieldErrors} =
    useContactSubmit({
      adId,
      contractId,
      pageName,
      enableAuthBeforeLead,
      subscriptionCommitment,
      onContact
    })

  const setIsOpen = useCallback(
    nextIsOpen => {
      if (!isDrawerControlled) {
        setIsOpenUncontrolled(nextIsOpen)
      }

      if (!nextIsOpen && hasContacted) {
        resetResult()
      }

      onDrawerOpenChange?.(nextIsOpen)
    },
    [isDrawerControlled, onDrawerOpenChange, hasContacted, resetResult]
  )

  const schema = useMemo(
    () =>
      createContactSchema({
        regexEmail: config.get('REGEX_EMAIL'),
        regexPhone: config.get('REGEX_PHONE'),
        regexPostalCode: config.get('REGEX_SPAIN_POSTALCODE'),
        isZipCodeEnabled,
        showTargetBuyerOptions,
        isTermsShown: !userData,
        isAlertsEnabled: enableAlertCreation
      }),
    [config, isZipCodeEnabled, showTargetBuyerOptions, enableAlertCreation, userData]
  )

  const runSchema = useCallback(
    values => {
      const fields = {}

      Object.keys(values).forEach(name => {
        const code = validateField({schema, name, value: values[name]})

        if (code) {
          fields[name] = code
        }
      })

      return Object.keys(fields).length > 0 ? {fields} : undefined
    },
    [schema]
  )

  const form = useAppForm({
    defaultValues: {
      [V2_FIELDS.MESSAGE]: EMPTY,
      [V2_FIELDS.NAME]: userData?.name ?? EMPTY,
      [V2_FIELDS.EMAIL]: userData?.email ?? EMPTY,
      [V2_FIELDS.PHONE]: userData?.phone ?? EMPTY,
      [V2_FIELDS.ZIP_CODE]: userData?.zipCode ?? EMPTY,
      [V2_FIELDS.TARGET_BUYER]: targetBuyer ?? DEFAULT_TARGET_BUYER,
      [V2_FIELDS.TERMS]: false,
      [V2_FIELDS.ENABLE_ALERTS]: false,
      [V2_FIELDS.BUYER_INTENTIONS]: []
    },
    validators: {
      onChange: ({value}) => runSchema(value),
      onSubmit: ({value}) => runSchema(value)
    },
    onSubmit: ({value}) =>
      submitBehindLoginGate({
        value,
        captchaTokenRef,
        submitContact,
        isPrivateSeller,
        isLogged,
        isLoginModalOpen,
        config,
        triggerLoginModal,
        authenticationHitInformation2
      })
  })

  const isDirty = useStore(form.store, state => state.isDirty)
  const isSubmitting = useStore(form.store, state => state.isSubmitting)
  const isValidating = useStore(form.store, state => state.isValidating)
  const submissionAttempts = useStore(form.store, state => state.submissionAttempts)
  const termsError = useStore(form.store, state => state.fieldMeta?.[V2_FIELDS.TERMS]?.errors?.length > 0)
  const hasSchemaErrors = useStore(form.store, state => !state.isValid)

  const targetBuyerOptions = useMemo(() => {
    const {TARGET_BUYER_IDS} = config.get('subscription')

    return [
      {id: TARGET_BUYER_IDS.PRIVATES, labelKey: 'FORM.TARGET_BUYER_PRIVATES'},
      {id: TARGET_BUYER_IDS.BUSINESS, labelKey: 'FORM.TARGET_BUYER_BUSINESS'}
    ]
  }, [config])

  useTargetBuyerSync({targetBuyer, form})

  const applyPrefill = useCallback(
    ({values, areTermsAccepted}) => {
      const asPrefill = {dontUpdateMeta: true}

      Object.entries(values).forEach(([name, value]) => {
        if (value) {
          form.setFieldValue(name, value, asPrefill)
        }
      })

      form.setFieldValue(V2_FIELDS.TERMS, areTermsAccepted, asPrefill)
      setIsPrefilled(true)
    },
    [form]
  )

  useUserDataPrefill({userData, applyPrefill})

  useLastContactPrefill({domain, userData, applyPrefill})

  const isBuyerIntentionsEnabled = Boolean(isLeadEnrichmentEnabled && isVOorVNorKM0 && !isPrivateSeller)
  const {restoredBuyerIntentions, persistBuyerIntentions} = useBuyerIntentionsStorage({
    enabled: isBuyerIntentionsEnabled
  })
  const initialBuyerIntentions = presetBuyerIntentions?.length ? presetBuyerIntentions : restoredBuyerIntentions

  useBuyerIntentionsRestore({initialBuyerIntentions, form, onBuyerIntentionsChange})

  usePresetMessageSync({presetMessage, form})

  useAlertAfterLead({hasContacted, isLogged, domain, alertSearch, adId, form, logger, onAlertCreated})

  const submitForm = useCallback(() => {
    if (hasMissingRequiredFields({schema, values: form.state.values})) {
      const {LEAD_ERROR_HIT_INFORMATIONS} = config.get('tracking')
      onLeadError?.({hitInformation: LEAD_ERROR_HIT_INFORMATIONS.MISSING_FIELDS})
    }

    form.handleSubmit()
  }, [schema, form, onLeadError, config])

  const handleCaptchaSubmit = useCallback(
    token => {
      if (!token) {
        return
      }

      captchaTokenRef.current = token
      resetResult()
      submitForm()
    },
    [resetResult, submitForm]
  )

  const handleUnblockAndSubmit = () =>
    unblockSellerAndResubmit({
      domain,
      config,
      logger,
      adId,
      pageName,
      sellerId,
      setHasUnblockError,
      setIsBlocked,
      resetResult,
      submitForm
    })

  const handleSubmit = event => {
    event.preventDefault()

    if (isBlocked) {
      setIsBlockedToastVisible(true)
      setBlockedSubmitAttempts(attempts => attempts + 1)

      return
    }

    resetResult()
    submitForm()
  }

  const formState = resolveFormState({
    isBlocked,
    hasContacted,
    hasGenericError,
    isSubmitting,
    isValidating,
    hasFieldErrors: (submissionAttempts > 0 && hasSchemaErrors) || Object.keys(fieldErrors).length > 0,
    isDirty,
    isPrefilled
  })

  let resolvedSubmitError = null

  if (hasUnblockError) {
    resolvedSubmitError = 'unblock'
  } else if (hasGenericError) {
    resolvedSubmitError = 'generic'
  }

  useSubmitErrorNotification({resolvedSubmitError, onSubmitError})

  const hasPhone = Boolean(form.getFieldValue(V2_FIELDS.PHONE))

  const successContent = <SuccessState contractId={contractId} hasPhone={hasPhone} isRenting={isRenting} />

  const resultStatus = (
    <div className={`${BASE_CLASS}-resultStatus`} role="status">
      {hasContacted && i18n.t(resolveSuccessLiteral({contractId, hasPhone, appName: config.get('APP_NAME')}))}
    </div>
  )

  const beforeSubmit = (
    <>
      <div className={`${BASE_CLASS}-blockedStatus`} role="status">
        <BlockedUserToast
          key={blockedSubmitAttempts}
          isToastVisible={isBlockedToastVisible}
          onCloseToast={setIsBlockedToastVisible}
          handleUnblockAndSubmitContactButton={handleUnblockAndSubmit}
          notificationStatus={isBlocked ? BLOCKED_TOAST_STATUS.BLOCKED : BLOCKED_TOAST_STATUS.UNBLOCKED}
          pageName={pageName}
        />
      </div>
      {hasCaptchaError && (
        <Body1 className={`${BASE_CLASS}-captchaError`} role="alert">
          {i18n.t('COMMON.SOMETHING_WENT_WRONG')}
        </Body1>
      )}
      {isCaptchaActive && (
        <div className={`${BASE_CLASS}-captcha`}>
          <FormCaptcha locale={i18n.locale} siteKey={captchaSiteKey} onSubmit={handleCaptchaSubmit} />
        </div>
      )}
    </>
  )

  const isPrivateSellerWithoutPhone = isPrivateSeller && hidePhone

  const hasTermsAlert = submissionAttempts > 0 && termsError

  const formContextValue = useMemo(
    () => ({
      domainErrors: fieldErrors,
      onDomainErrorClear: clearFieldError,
      targetBuyerOptions,
      hasTermsAlert,
      persistBuyerIntentions
    }),
    [fieldErrors, clearFieldError, targetBuyerOptions, hasTermsAlert, persistBuyerIntentions]
  )

  const body = (
    <form className={`${BASE_CLASS}-form`} onSubmit={handleSubmit}>
      <form.AppForm>
        <ContactFormContextProvider value={formContextValue}>
          <ContactBody
            buttonGroup={isBottomDrawer ? undefined : buttonGroup}
            showMessageTitle={!isBottomDrawer && !isPrivateSellerWithoutPhone}
            beforeSubmit={beforeSubmit}
          />
        </ContactFormContextProvider>
      </form.AppForm>
    </form>
  )

  if (formPresentation === FORM_PRESENTATIONS.DRAWER) {
    let drawerTitleKey
    if (isBottomDrawer || isPrivateSellerWithoutPhone) {
      drawerTitleKey = getMessageTitleKey(config.get('APP_NAME'))
    }

    return (
      <div className={BASE_CLASS} data-state={formState}>
        <Button
          ref={openDrawerTriggerRef}
          type="button"
          variant={BUTTON_VARIANTS.SOLID}
          intent={BUTTON_INTENTS.ACCENT}
          size={BUTTON_SIZES.LARGE}
          shape={BUTTON_SHAPES.CIRCLE}
          fullWidth
          onClick={() => {
            onSendMessageClick?.()
            setIsOpen(true)
          }}
        >
          {i18n.t('CONTACT_SELLER_V2.FORM.SUBMIT_CTA')}
        </Button>
        <FormDrawer
          hasContacted={hasContacted}
          onOpenChange={setIsOpen}
          open={isOpen}
          titleKey={drawerTitleKey}
          triggerRef={openDrawerTriggerRef}
        >
          {resultStatus}
          {hasContacted ? successContent : body}
        </FormDrawer>
      </div>
    )
  }

  return (
    <div className={BASE_CLASS} data-state={formState}>
      {resultStatus}
      {hasContacted ? successContent : body}
    </div>
  )
}

ContactForm.displayName = 'ContactForm'

ContactForm.propTypes = {
  buttonGroup: PropTypes.node
}

export default ContactForm
